import React, {
	useEffect,
	useMemo,
	useRef,
	useState,
	startTransition,
	useCallback,
} from "react";
import { Virtuoso } from "react-virtuoso";
import {
	LineChart,
	Line,
	AreaChart,
	Area,
	CartesianGrid,
	XAxis,
	YAxis,
	ResponsiveContainer,
	ScatterChart,
	Tooltip,
	Scatter,
	ReferenceLine,
} from "recharts";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import {
	Box,
	CircularProgress,
	Paper,
	Typography,
	Stack,
	FormGroup,
	FormControlLabel,
	Checkbox,
	Divider,
	Button,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	Chip,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import DateTimeRangePicker from "@wojtekmaj/react-datetimerange-picker";
import "@wojtekmaj/react-datetimerange-picker/dist/DateTimeRangePicker.css";
import "react-calendar/dist/Calendar.css";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import IconButton from "@mui/material/IconButton";

dayjs.extend(utc);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const BACKEND_TOKEN = process.env.REACT_APP_BACKEND_TOKEN;

const GRID = { strokeDasharray: "3 3" };
const X_TICK = { angle: 0, textAnchor: "middle", fontSize: 10 };
const MARGIN_AREA = { top: 0, right: 0, left: 0, bottom: 0 };
const MARGIN_LINE = { top: 0, right: 0, left: 0, bottom: 0 };
const MARGIN_SCATTER = { top: 24, right: 10, left: 10, bottom: 0 };

const colorCache = new Map();
const PALETTE = [
	"#1f77b4",
	"#ff7f0e",
	"#2ca02c",
	"#d62728",
	"#9467bd",
	"#8c564b",
	"#e377c2",
	"#7f7f7f",
	"#bcbd22",
	"#17becf",
];
const EVENT_COLORS = { 0: "#27ae60", 1: "#c0392b" };

const SYNC_ID = "history-sync";

function colorFor(idx) {
	if (colorCache.has(idx)) return colorCache.get(idx);
	const c = PALETTE[idx % PALETTE.length];
	colorCache.set(idx, c);
	return c;
}

export default function History({ deviceId, chassisNum }) {
	const [graphs, setGraphs] = useState([]);
	const [stage, setStage] = useState(null);
	const [loading, setLoading] = useState(false);
	const [from, setFrom] = useState(dayjs().subtract(1, "day"));
	const [to, setTo] = useState(dayjs());
	const [range, setRange] = useState([from.toDate(), to.toDate()]);
	const [groupOptions, setGroupOptions] = useState([]);
	const [fieldOptions, setFieldOptions] = useState([]);
	const [order, setOrder] = useState([]);
	const [collapsed, setCollapsed] = useState({});
	const [filtersOpen, setFiltersOpen] = useState(false);
	const [bookmarks, setBookmarks] = useState([]);
	const [onlyActiveSeries, setOnlyActiveSeries] = useState(false);

	const graphsRef = useRef({});
	const inflightRef = useRef(null);
	const runIdRef = useRef(0);
	const hydratedRunRef = useRef(null);

	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

	const rangeValid = from && to && from.isBefore(to);

	const msFrom = useMemo(() => from.valueOf(), [from]);
	const msTo = useMemo(() => to.valueOf(), [to]);

	const enabledGroups = useMemo(
		() => groupOptions.filter((o) => o.enabled).map((o) => o.id),
		[groupOptions]
	);
	const enabledFields = useMemo(
		() => fieldOptions.filter((o) => o.enabled).map((o) => o.id),
		[fieldOptions]
	);

	const toggleGroup = (id) =>
		setGroupOptions((prev) =>
			prev.map((o) => (o.id === id ? { ...o, enabled: !o.enabled } : o))
		);
	const toggleField = (id) =>
		setFieldOptions((prev) =>
			prev.map((o) => (o.id === id ? { ...o, enabled: !o.enabled } : o))
		);

	const applyFilters = () => {
		fetchStage0();
		setFiltersOpen(false);
	};

	useEffect(() => () => inflightRef.current?.abort(), []);

	const handleRangeChange = (val) => {
		if (!val) return;
		let [start, end] = val;
		const now = new Date();

		if (end && end > now) end = now; // cap end at now
		if (start && end && start > end) start = end; // keep start ≤ end

		setFrom(start ? dayjs(start) : from);
		setTo(end ? dayjs(end) : to);
		setRange([start || null, end || null]);
	};

	const fetchStage0 = async () => {
		runIdRef.current += 1;
		inflightRef.current?.abort();
		setGraphs([]);
		setStage(null);
		hydratedRunRef.current = null;
		if (!deviceId || !rangeValid) return;
		graphsRef.current = {};
		await fetchStage({ runId: runIdRef.current });
	};

	useEffect(() => {
		fetchStage0();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [deviceId, msFrom, msTo]);

	const fetchStage = useCallback(
		async ({ every, offset, runId }) => {
			setLoading(true);
			const controller = new AbortController();
			inflightRef.current = controller;

			try {
				const url = new URL(
					`${BACKEND_URL}devices/${deviceId}/history/`
				);
				url.searchParams.set("from", dayjs(msFrom).utc().toISOString());
				url.searchParams.set("to", dayjs(msTo).utc().toISOString());
				if (every) url.searchParams.set("every", every);
				if (offset) url.searchParams.set("offset", offset);
				url.searchParams.set("groups", enabledGroups.join(","));
				url.searchParams.set("fields", enabledFields.join(","));

				const res = await fetch(url.toString(), {
					headers: {
						Accept: "application/json",
						Authorization: `Bearer ${BACKEND_TOKEN}`,
					},
					signal: controller.signal,
				});
				const payload = await res.json();
				const data = payload.data;

				if (runId === runIdRef.current && !controller.signal.aborted) {
					const merged = mergeGraphs(graphsRef.current, data.graphs);
					graphsRef.current = merged;

					const mergedArr = Object.values(merged);
					startTransition(() => {
						setGraphs(mergedArr);
						setOrder((prev) => {
							const seen = new Set(prev);
							const next = [...prev];
							for (const g of mergedArr)
								if (!seen.has(g.id)) next.push(g.id);
							return next.filter((id) => merged[id]);
						});
					});

					setStage(data.stage);

					const sentEmptyFilters =
						enabledGroups.length === 0 &&
						enabledFields.length === 0;
					if (
						sentEmptyFilters &&
						hydratedRunRef.current !== runId &&
						data?.filters
					) {
						if (Array.isArray(data.filters.group_options))
							setGroupOptions(data.filters.group_options);
						if (Array.isArray(data.filters.field_options))
							setFieldOptions(data.filters.field_options);
						hydratedRunRef.current = runId;
					}
				}
			} finally {
				if (runId === runIdRef.current) setLoading(false);
			}
		},
		[deviceId, msFrom, msTo, enabledGroups, enabledFields]
	);

	const handleRefine = useCallback(() => {
		if (!stage) return;
		fetchStage({
			every: stage.next_every,
			offset: stage.next_offset,
			runId: runIdRef.current,
		});
	}, [stage, fetchStage]);

	useEffect(() => {
		if (stage && !stage.done) handleRefine();
	}, [stage, handleRefine]);

	const move = useCallback((id, dir) => {
		setOrder((prev) => {
			const idx = prev.indexOf(id);
			if (idx < 0) return prev;
			const j = dir === "up" ? idx - 1 : idx + 1;
			if (j < 0 || j >= prev.length) return prev;
			const next = prev.slice();
			[next[idx], next[j]] = [next[j], next[idx]];
			return next;
		});
	}, []);

	const toggleCollapsed = useCallback((id) => {
		setCollapsed((c) => ({ ...c, [id]: !c[id] }));
	}, []);

	const addBookmark = useCallback((ms) => {
		if (typeof ms !== "number" || Number.isNaN(ms)) return;
		setBookmarks((prev) => {
			if (prev.includes(ms)) return prev;
			const next = [...prev, ms].sort((a, b) => a - b);
			return next;
		});
	}, []);

	const removeBookmark = useCallback((ms) => {
		setBookmarks((prev) => prev.filter((x) => x !== ms));
	}, []);

	return (
		<Box>
			<Stack
				direction="row"
				alignItems="center"
				justifyContent="space-between"
				mb={isMobile ? 0.5 : 1}
			>
				<Typography variant="subtitle1" sx={{ gap: 1 }}>
					Historical Data{" "}
					<Typography variant="caption">{chassisNum}</Typography>
				</Typography>
				{!!bookmarks.length && (
					<Stack direction="row" spacing={0.5} flexWrap="wrap">
						{bookmarks.map((ms) => (
							<Chip
								key={ms}
								size="small"
								label={dayjs(ms).format("HH:mm:ss")}
								onDelete={() => removeBookmark(ms)}
							/>
						))}
					</Stack>
				)}
			</Stack>

			<LocalizationProvider dateAdapter={AdapterDayjs}>
				<Stack
					direction={{ xs: "column", sm: "row" }}
					spacing={isMobile ? 1 : 2}
					alignItems="center"
					mb={isMobile ? 1 : 2}
					sx={{ px: isMobile ? 0.5 : 0 }}
				>
					<DateTimeRangePicker
						onChange={handleRangeChange}
						value={range}
						maxDate={new Date()}
						disableClock
						clearIcon={null}
					/>
					<FormControlLabel
						control={
							<Checkbox
								size="small"
								checked={onlyActiveSeries}
								onChange={(e) =>
									setOnlyActiveSeries(e.target.checked)
								}
							/>
						}
						label="Hide inactive faults"
					/>
					{loading && <CircularProgress size={18} />}
				</Stack>
			</LocalizationProvider>

			<Accordion
				sx={{
					mb: isMobile ? 1 : 2,
					borderRadius: isMobile ? 0.5 : 1,
					"&:before": { display: "none" },
				}}
				expanded={filtersOpen}
				onChange={(_, open) => setFiltersOpen(open)}
			>
				<AccordionSummary
					expandIcon={<ExpandMoreIcon />}
					aria-controls="filters-content"
					id="filters-header"
				>
					<Stack direction="row" spacing={1} alignItems="center">
						<Typography variant="subtitle1">Filters</Typography>
						<Chip
							size="small"
							label={`Groups ${enabledGroups.length}/${groupOptions.length}`}
						/>
						<Chip
							size="small"
							label={`Singles ${enabledFields.length}/${fieldOptions.length}`}
						/>
						{loading ? <CircularProgress size={14} /> : null}
					</Stack>
				</AccordionSummary>

				<AccordionDetails sx={{ p: isMobile ? 1 : 2 }}>
					<Stack
						direction={{ xs: "column", md: "row" }}
						spacing={2}
						alignItems="flex-start"
					>
						<Box sx={{ minWidth: 260 }}>
							<Typography variant="subtitle2" gutterBottom>
								Groups
							</Typography>
							<FormGroup>
								{groupOptions.map((opt) => (
									<FormControlLabel
										key={opt.id}
										control={
											<Checkbox
												size="small"
												checked={!!opt.enabled}
												onChange={() =>
													toggleGroup(opt.id)
												}
											/>
										}
										label={opt.title}
									/>
								))}
							</FormGroup>
						</Box>

						<Divider
							flexItem
							orientation="vertical"
							sx={{ display: { xs: "none", md: "block" } }}
						/>

						<Box sx={{ minWidth: 260 }}>
							<Typography variant="subtitle2" gutterBottom>
								Singles
							</Typography>
							<FormGroup>
								{fieldOptions.map((opt) => (
									<FormControlLabel
										key={opt.id}
										control={
											<Checkbox
												size="small"
												checked={!!opt.enabled}
												onChange={() =>
													toggleField(opt.id)
												}
											/>
										}
										label={opt.title}
									/>
								))}
							</FormGroup>
						</Box>

						<Box sx={{ flex: 1 }} />
						<Stack direction="row" spacing={1}>
							<Button
								size="small"
								variant="contained"
								onClick={applyFilters}
							>
								Apply
							</Button>
						</Stack>
					</Stack>
				</AccordionDetails>
			</Accordion>

			{graphs.length === 0 && !loading && (
				<Typography variant="body2" color="text.secondary">
					No data
				</Typography>
			)}

			<Virtuoso
				useWindowScroll
				style={{ height: "auto" }}
				totalCount={order.length}
				computeItemKey={(index) => order[index] ?? index}
				increaseViewportBy={{ top: 400, bottom: 800 }}
				itemContent={(index) => {
					const gid = order[index];
					const g = graphs.find((x) => x.id === gid);
					if (!g) return null;
					return (
						<Box sx={{ p: isMobile ? 0.5 : 1 }}>
							<Paper
								sx={{
									p: isMobile ? 1 : 2,
									borderRadius: isMobile ? 1 : 2,
									transition: "box-shadow 120ms ease",
									"&:hover": { boxShadow: 4 },
								}}
								elevation={1}
							>
								<Stack
									direction="row"
									alignItems="center"
									justifyContent="space-between"
									sx={{ mb: 1 }}
								>
									<Stack
										direction="row"
										spacing={1}
										alignItems="center"
									>
										<IconButton
											size="small"
											onClick={() =>
												toggleCollapsed(g.id)
											}
											aria-label={
												collapsed[g.id]
													? "Expand"
													: "Collapse"
											}
											sx={{
												transform: collapsed[g.id]
													? "rotate(-90deg)"
													: "none",
												transition:
													"transform 120ms linear",
											}}
										>
											<ExpandMoreIcon />
										</IconButton>
										<Typography
											variant="h6"
											sx={{
												fontSize: {
													xs: "0.95rem",
													sm: "1.25rem",
												}, // smaller on mobile
												fontWeight: 600,
												lineHeight: 1.2,
											}}
										>
											{g.title}
										</Typography>
									</Stack>
									<Stack
										direction="row"
										spacing={isMobile ? 0 : 0.5}
									>
										<IconButton
											size="small"
											onClick={() => move(g.id, "up")}
											aria-label="Move up"
										>
											<KeyboardArrowUpIcon />
										</IconButton>
										<IconButton
											size="small"
											onClick={() => move(g.id, "down")}
											aria-label="Move down"
										>
											<KeyboardArrowDownIcon />
										</IconButton>
									</Stack>
								</Stack>
								{!collapsed[g.id] ? (
									<GraphRenderer
										graph={g}
										msFrom={msFrom}
										msTo={msTo}
										bookmarks={bookmarks}
										onAddBookmark={addBookmark}
										onlyActiveSeries={onlyActiveSeries}
										isMobile={isMobile}
									/>
								) : null}
							</Paper>
						</Box>
					);
				}}
			/>
		</Box>
	);
}

/* ----------------------------- helpers ----------------------------- */

function mergeGraphs(existingById, newGraphs) {
	const out = { ...existingById };
	for (const g of newGraphs || []) {
		const prev = out[g.id];
		if (!prev) {
			out[g.id] = g;
			continue;
		}
		const mergedSeries = [];
		for (const ns of g.series) {
			const ps = prev.series.find((x) => x.field === ns.field);
			if (!ps) {
				mergedSeries.push(ns);
				continue;
			}
			const mergedPoints = dedupeByT([...ps.points, ...ns.points]);
			mergedSeries.push({ ...ps, points: mergedPoints });
		}
		for (const ps of prev.series) {
			if (!mergedSeries.find((x) => x.field === ps.field))
				mergedSeries.push(ps);
		}
		out[g.id] = { ...prev, series: mergedSeries };
	}
	return out;
}

function dedupeByT(points) {
	const m = new Map();
	for (const p of points) m.set(p.t, p.v);
	return Array.from(m.entries())
		.sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
		.map(([t, v]) => ({ t, v }));
}

function buildRowsForRecharts(graph) {
	const maps = (graph.series || []).map((s) => {
		const m = new Map();
		for (const p of s.points || []) {
			const t = typeof p.t === "number" ? p.t : Date.parse(p.t);
			m.set(t, p.v);
		}
		return { field: s.field, map: m };
	});

	const allTs = new Set();
	for (const { map } of maps) for (const t of map.keys()) allTs.add(t);
	const stamps = Array.from(allTs).sort((a, b) => a - b);

	return stamps.map((t) => {
		const row = { t };
		for (const { field, map } of maps) {
			if (map.has(t)) row[field] = map.get(t);
		}
		return row;
	});
}

function computeYDomain(graph) {
	const ranges = (graph.series || [])
		.map((s) => (Array.isArray(s.range) ? s.range : null))
		.filter(Boolean);
	if (!ranges.length) return null;
	const ymin = Math.min(...ranges.map((r) => r[0]));
	const ymax = Math.max(...ranges.map((r) => r[1]));
	return [ymin, ymax];
}

/* --------------------------- chart renderer --------------------------- */

const EventTooltip = ({ active, payload }) => {
	if (!active || !payload?.length) return null;
	const p0 = payload[0];
	const ts = Number(p0?.payload?.x);
	const isActive = !!p0?.payload?.value;
	const color = isActive ? EVENT_COLORS[1] : EVENT_COLORS[0];
	const seriesName = p0?.payload?.label || p0?.name || "";

	return (
		<Paper sx={{ p: 1 }}>
			<Typography variant="caption" sx={{ display: "block" }}>
				{dayjs(ts).format("YYYY-MM-DD HH:mm:ss")}
			</Typography>
			<Typography sx={{ fontWeight: 700, color }}>
				{isActive ? "ACTIVE" : "OK"}
			</Typography>
			<Typography variant="body2">{seriesName}</Typography>
		</Paper>
	);
};

const GraphRenderer = React.memo(function GraphRenderer({
	graph,
	msFrom,
	msTo,
	bookmarks = [],
	onAddBookmark,
	onlyActiveSeries = false,
	isMobile = false,
}) {
	const rows = useMemo(() => buildRowsForRecharts(graph), [graph]);
	const yDomain = useMemo(() => computeYDomain(graph), [graph]);

	if (!rows.length) {
		return (
			<Typography variant="body2" color="text.secondary">
				No data
			</Typography>
		);
	}

	if (graph.graph_type === "area") {
		return (
			<ResponsiveContainer width="100%" height={isMobile ? 220 : 280}>
				<AreaChart
					data={rows}
					margin={MARGIN_AREA}
					syncId={SYNC_ID}
					syncMethod="value"
					onClick={(e) => onAddBookmark?.(extractTs(e))}
				>
					<CartesianGrid {...GRID} />
					<XAxis
						dataKey="t"
						type="number"
						domain={[msFrom, msTo]}
						scale="time"
						allowDataOverflow
						minTickGap={24}
						tick={X_TICK}
						height={48}
						tickMargin={6}
						tickFormatter={(ms) =>
							dayjs(ms).format("YYYY-MM-DD HH:mm")
						}
					/>
					<YAxis domain={yDomain || undefined} />
					<Tooltip
						cursor={{ strokeDasharray: "3 3" }}
						formatter={(v) => v}
						labelFormatter={(ms) =>
							dayjs(ms).format("YYYY-MM-DD HH:mm:ss")
						}
					/>
					{bookmarks.map((ms) => (
						<ReferenceLine
							key={ms}
							x={ms}
							stroke="#9c27b0"
							strokeDasharray="4 4"
							isFront
						/>
					))}
					{graph.series.map((s, idx) => (
						<Area
							key={s.field}
							type="monotone"
							dataKey={s.field}
							name={`${s.name}${s.unit ? ` (${s.unit})` : ""}`}
							dot={false}
							strokeWidth={1.5}
							fillOpacity={0.18}
							stroke={colorFor(idx)}
							fill={colorFor(idx)}
						/>
					))}
				</AreaChart>
			</ResponsiveContainer>
		);
	}

	if (graph.graph_type === "event") {
		const baseSeries = onlyActiveSeries
			? (graph.series || []).filter((s) =>
					(s.points || []).some((p) => !!p.v)
			  )
			: graph.series || [];

		// nothing to show after filter
		if (baseSeries.length === 0) {
			return (
				<Typography variant="body2" color="text.secondary">
					No active faults
				</Typography>
			);
		}

		const series = baseSeries.flatMap((s, lane) => {
			const base = (s.points || []).map((p) => ({
				x: typeof p.t === "number" ? p.t : Date.parse(p.t),
				y: lane,
				value: p.v,
				label: s.name,
			}));
			return [
				{
					name: `${s.name} OK`,
					lane,
					data: base.filter((p) => !p.value),
					fill: EVENT_COLORS[0],
				},
				{
					name: `${s.name} ACTIVE`,
					lane,
					data: base.filter((p) => p.value),
					fill: EVENT_COLORS[1],
				},
			];
		});

		// use filtered length for height and domain; half-step trims top/bottom padding
		const lanes = baseSeries.length;
		const chartHeight = Math.max(
			isMobile ? 120 : 160,
			(isMobile ? 44 : 56) * lanes
		);

		return (
			<ResponsiveContainer width="100%" height={chartHeight}>
				<ScatterChart
					margin={
						isMobile
							? { top: 12, right: 8, left: 8, bottom: 0 }
							: MARGIN_SCATTER
					}
					syncId={SYNC_ID}
					syncMethod="value"
					onClick={(e) => onAddBookmark?.(extractTs(e))}
				>
					<CartesianGrid {...GRID} />
					<XAxis
						dataKey="x"
						type="number"
						scale="time"
						domain={[msFrom, msTo]}
						allowDataOverflow
						tickFormatter={(ms) =>
							dayjs(ms).format("YYYY-MM-DD HH:mm")
						}
						minTickGap={24}
						tick={X_TICK}
						height={48}
						tickMargin={6}
					/>
					<YAxis
						type="number"
						dataKey="y"
						domain={[-0.5, lanes - 0.5]} // <<< shrink to filtered lanes
						ticks={Array.from({ length: lanes }, (_, i) => i)}
						tickFormatter={(i) => baseSeries[i]?.name ?? ""}
						tick={{ fontSize: 11 }}
						padding={{ top: 0, bottom: 0 }} // <<< remove extra padding
						allowDataOverflow
					/>
					<Tooltip
						cursor={{ strokeDasharray: "3 3" }}
						content={<EventTooltip />}
					/>
					{bookmarks.map((ms) => (
						<ReferenceLine
							key={ms}
							x={ms}
							stroke="#9c27b0"
							strokeDasharray="4 4"
							isFront
						/>
					))}
					{series.map((s) => (
						<Scatter
							key={s.name}
							name={s.name}
							data={s.data}
							fill={s.fill}
							shape="square"
							legendType="square"
						/>
					))}
				</ScatterChart>
			</ResponsiveContainer>
		);
	}

	const isStep = graph.graph_type === "step";

	return (
		<ResponsiveContainer width="100%" height={280}>
			<LineChart
				data={rows}
				margin={MARGIN_LINE}
				syncId={SYNC_ID}
				syncMethod="value"
				onClick={(e) => onAddBookmark?.(extractTs(e))}
			>
				<CartesianGrid {...GRID} />
				<XAxis
					dataKey="t"
					type="number"
					domain={[msFrom, msTo]}
					scale="time"
					allowDataOverflow
					minTickGap={24}
					tick={X_TICK}
					tickFormatter={(ms) => dayjs(ms).format("YYYY-MM-DD HH:mm")}
					height={48}
					tickMargin={6}
				/>
				<YAxis domain={yDomain || undefined} />
				<Tooltip
					cursor={{ strokeDasharray: "3 3" }}
					formatter={(v) => v}
					labelFormatter={(ms) =>
						dayjs(ms).format("YYYY-MM-DD HH:mm:ss")
					}
				/>
				{bookmarks.map((ms) => (
					<ReferenceLine
						key={ms}
						x={ms}
						stroke="#9c27b0"
						strokeDasharray="4 4"
						isFront
					/>
				))}
				{graph.series.map((s, idx) => (
					<Line
						key={s.field}
						type={isStep ? "stepAfter" : "monotone"}
						dataKey={s.field}
						name={`${s.name}${s.unit ? ` (${s.unit})` : ""}`}
						dot={false}
						strokeWidth={1.5}
						stroke={colorFor(idx)}
					/>
				))}
			</LineChart>
		</ResponsiveContainer>
	);
});

const extractTs = (e) =>
	e?.activeLabel ??
	e?.activePayload?.[0]?.payload?.x ??
	e?.activePayload?.[0]?.payload?.t;
